import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {Subject} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {mergeMap, switchMap, takeUntil} from 'rxjs/operators';
import {
    CustomerType,
    MetaObject,
    ReclamationBase,
    ReclamationService,
    User, UserDetailsService,
    UserService,
    UtilsService
} from '../../../shared';

/**
 * Meta Model for a Reclamation
 */
interface ReclamationMeta extends MetaObject {
    meta?: {
        total: number;
        user: User;
    };
}

@Component({
    selector: 'sl-reclamation-list',
    templateUrl: './reclamation-list.component.html',
    styles: [],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReclamationListComponent implements OnInit, OnDestroy {

    private readonly SUPPORT_PAGE_LENGTH = 10;
    private readonly REKLA_PAGE_LENGTH = 25;

    public loading: boolean;
    public list: ReclamationBase[] = undefined;
    public userId: string;
    public collectionSize: number;
    public page: number = 1;
    public pageLength: number;
    public username: string;
    public isSupportApp: boolean;

    private _unsubscribe = new Subject();
    private user: User;

    /**
     *
     * @param reclamationService
     * @param userService
     * @param CD
     * @param router
     * @param Utils
     * @param Route
     * @param route
     * @param userDetails
     */
    constructor(private reclamationService: ReclamationService,
                private userService: UserService,
                private CD: ChangeDetectorRef,
                private router: Router,
                private Utils: UtilsService,
                private Route: ActivatedRoute,
                private route: ActivatedRoute,
                private userDetails: UserDetailsService) {
    }

    /**
     * @ignore
     */
    ngOnInit(): void {
        this.getReclamationsForCurrentPage();
        this.getUserDetails();
    }

    /**
     * @ignore
     */
    ngOnDestroy(): void {
        this._unsubscribe.next();
        this._unsubscribe.complete();
    }

    /**
     * Pagination navigation
     */
    public onPageChange(): void {
        this.navigate();
    }

    /**
     * On reclamation click
     * @param reclamation
     */
    public onReclamationClick(reclamation: ReclamationBase): void {
        this.isSupportApp
            ? this.router.navigate(['/user/reclamationsdetail/' + reclamation.id])
            : this.router.navigate([reclamation.id], {
                relativeTo: this.Route
            });
    }

    /**
     * Navigate to a new reclamation
     */
    public startNewReclamation(): void {
        this.isSupportApp && this.user?.customerType === CustomerType.Private
            ? this.router.navigateByUrl('/produkte/identifikation/' + this.userId)
            : this.router.navigateByUrl('/produkte/start/' + this.userId)
    }

    /**
     * Navigate to a different page in the List
     */
    private navigate(): void {
        const routerParams: any = {};

        this.Utils.addPageToRouterParams(routerParams, this.Route.parent.queryParams, this.page);

        this.router.navigate([], {
            relativeTo: this.Route,
            queryParams: routerParams
        });
    }

    /**
     * Get the reclamations for the current page
     */
    private getReclamationsForCurrentPage(): void {
        this.Route.queryParams.pipe(switchMap(queryParams => {
            this.page = Number(queryParams.p || 1);
            this.userId = this.Route.snapshot.params.id;
            this.isSupportApp = !!this.userId;
            this.pageLength = this.isSupportApp ? this.SUPPORT_PAGE_LENGTH : this.REKLA_PAGE_LENGTH;
            this.loading = true;
            return this.userId
                ? this.userService.getReclamations(this.userId, this.pageLength, this.page)
                : this.reclamationService.getReclamationList(this.pageLength, this.page);
        }), takeUntil(this._unsubscribe))
            .subscribe((reclamations: ReclamationBase[] & ReclamationMeta) => {
                this.list = reclamations;
                if (reclamations.$meta) {
                    this.collectionSize = reclamations.$meta.total;
                    this.username = reclamations.$meta.user ? reclamations.$meta.user.username : null;
                }
                this.loading = false;
                this.CD.markForCheck();
            }, () => {
                this.loading = false;
                this.CD.markForCheck();
            });
    }

    /**
     * Get the details of the user so that the user's customerType can be checked for
     * where to route to when a new reclamation is selected
     * @private
     */
    private getUserDetails(): void {
        this.route.params.pipe(
            mergeMap(params => this.userDetails.getUser(params.id)),
            takeUntil(this._unsubscribe)
        ).subscribe((user: User) => {
            this.user = user;
            this.CD.markForCheck();
        });
    }

}
